<template>
    <!-- Alarm profile summary, presented the way the alarm snapshot page does it so both screens
         describe a profile identically:
           - a red chip with an up arrow for the high threshold, blue with a down arrow for the low
             (alarm and caution share an end's chip, e.g. "36, 38")
           - a bell per alarm type, green when enabled and red when not
           - a refresh icon per alarm type for auto-resolve, greyed out when its parent alarm is off

         Laid out inline rather than stacked, because this sits in a dense editable grid row.

         The setpoints and the enabled/disabled states are each wrapped in their own nowrap group so
         the row can only break *between* the two. Letting all four children wrap freely put the
         threshold group on the setpoint line and pushed the offline group down alone, which reads as
         though the two status groups are unrelated -- they belong together on one line.

         The states render for every profile, including one with no thresholds at all: "no alarms
         configured" and "alarms configured but switched off" are different situations on a screen
         that reassigns alarm profiles in bulk, and leaving the row blank made them look identical. -->
    <span class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <!-- Dropped entirely when the profile has neither setpoint, rather than left as an empty
             wrapper: a zero-width flex item still takes the parent's gap, which showed up as a
             stray indent before the state icons on a profile with no thresholds. -->
        <span v-if="hasSetpoints" class="flex flex-nowrap items-center gap-x-2">
            <span v-if="high !== null"
                  class="inline-flex items-center gap-0.5 rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                  title="High threshold (alarm, caution)">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M18 11l-6 -6"/><path d="M6 11l6 -6"/></svg>
                {{ high }}
            </span>

            <span v-if="low !== null"
                  class="inline-flex items-center gap-0.5 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                  title="Low threshold (alarm, caution)">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M18 13l-6 6"/><path d="M6 13l6 6"/></svg>
                {{ low }}
            </span>
        </span>

        <span class="flex flex-nowrap items-center gap-x-2">
            <!-- Threshold alarms + auto-resolve -->
            <span class="inline-flex items-center gap-1" :title="thresholdTitle">
                <span class="text-[10px] uppercase tracking-wide text-gray-400">Threshold</span>
                <span :class="thresholdAlarms ? 'text-green-500' : (isSilentByDesign ? 'text-gray-300' : 'text-red-500')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>
                </span>
                <span :class="thresholdAlarms && thresholdResolve ? 'text-green-500' : 'text-gray-300'">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/></svg>
                </span>
            </span>

            <!-- Offline alarms + auto-resolve -->
            <span class="inline-flex items-center gap-1" :title="offlineTitle">
                <span class="text-[10px] uppercase tracking-wide text-gray-400">Offline</span>
                <span :class="offlineAlarms ? 'text-green-500' : (isSilentByDesign ? 'text-gray-300' : 'text-red-500')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>
                </span>
                <span :class="offlineAlarms && offlineResolve ? 'text-green-500' : 'text-gray-300'">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/></svg>
                </span>
            </span>
        </span>
    </span>
</template>

<script>
export default {
    name: 'AlarmThresholdChips',
    props: {
        // Pre-formatted by the API: "46", or "46, 44" when an alarm and a caution share the end.
        // null when that end has no threshold that can actually fire.
        high: {
            type: [String, Number, null],
            default: null,
        },
        low: {
            type: [String, Number, null],
            default: null,
        },
        thresholdAlarms: {
            type: Boolean,
            default: false,
        },
        thresholdResolve: {
            type: Boolean,
            default: false,
        },
        offlineAlarms: {
            type: Boolean,
            default: false,
        },
        offlineResolve: {
            type: Boolean,
            default: false,
        },
        // Any threshold value on the profile, even one that cannot fire. Distinguishes a profile
        // that was configured then switched off from one that was never configured.
        hasAnyThreshold: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        hasSetpoints() {
            return this.high !== null || this.low !== null;
        },
        // A profile with nothing configured and nothing enabled is silent on purpose (a
        // "No Alarms" profile), not misconfigured, so its bells are greyed rather than red -- red
        // would read as "this is broken" on a profile working exactly as intended.
        //
        // Keyed off `hasAnyThreshold` rather than the rendered setpoints: a threshold with no
        // delay time is omitted from the chips (it can never fire) but the profile IS configured,
        // so it still shows red -- that is the case worth noticing.
        isSilentByDesign() {
            return ! this.hasAnyThreshold && ! this.thresholdAlarms && ! this.offlineAlarms;
        },
        thresholdTitle() {
            return `Threshold alarms ${this.thresholdAlarms ? 'enabled' : 'disabled'}`
                + `, auto-resolve ${this.thresholdAlarms && this.thresholdResolve ? 'enabled' : 'disabled'}`;
        },
        offlineTitle() {
            return `Offline alarms ${this.offlineAlarms ? 'enabled' : 'disabled'}`
                + `, auto-resolve ${this.offlineAlarms && this.offlineResolve ? 'enabled' : 'disabled'}`;
        },
    },
};
</script>
